import useAnalytics from "@helpers/useAnalytics";
import {
  IfIsaCtaProps,
  IfIsaCtaDismissedProps,
} from "@ifgengineering/client-analytics/lib/types";
import { useSelector } from "react-redux";
import { investorSelector } from "@state/investor/investor.selectors";
import { useTransferNavigation } from "@components/IfIsa/IfIsaTransfer/context/TransferNavigationContext";
import { IfIsaTransferInSubmitFormProps } from "@ifgengineering/client-analytics/src/types";
import { IsaTransferAmountTypeMap } from "@components/IfIsa/IfIsaTransfer/consts";
import { IfIsaTransferType } from "@ifgengineering/client-invest-sdk/src/types";

export const useIfIsaAnalytics = () => {
  const investor = useSelector(investorSelector);
  const isMember = investor?.isMember;
  const user_id = investor?.id;

  const fireIfIsaCtaViewed = useAnalytics("ifIsaCtaViewed");
  const triggerIfIsaCtaViewed = async (
    args: Omit<IfIsaCtaProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaCtaViewed)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaCtaClicked = useAnalytics("ifIsaCtaClicked");
  const triggerIfIsaCtaClicked = async (
    args: Omit<IfIsaCtaProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaCtaClicked)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaCtaDismissed = useAnalytics("ifIsaCtaDismissed");
  const triggerIfIsaCtaDismissed = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaCtaDismissed)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaPermanentPageViewed = useAnalytics("ifIsaPermanentPageViewed");
  const triggerIfIsaPermanentPageViewed = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaPermanentPageViewed)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaDescriptionPageViewed = useAnalytics(
    "ifIsaDescriptionPageViewed"
  );
  const triggerIfIsaDescriptionPageViewed = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaDescriptionPageViewed)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaProceedToDeclarationClicked = useAnalytics(
    "ifIsaProceedToDeclarationClicked"
  );
  const triggerIfIsaProceedToDeclarationClicked = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaProceedToDeclarationClicked)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaConfirmRegistrationClicked = useAnalytics(
    "ifIsaConfirmRegistrationClicked"
  );
  const triggerIfIsaConfirmRegistrationClicked = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaConfirmRegistrationClicked)({
      ...args,
      isMember,
      user_id,
    });
  };

  const fireIfIsaRegistrationSuccessViewed = useAnalytics(
    "ifIsaRegistrationSuccessViewed"
  );
  const triggerIfIsaRegistrationSuccessViewed = async (
    args: Omit<IfIsaCtaDismissedProps, "isMember" | "user_id">
  ) => {
    (await fireIfIsaRegistrationSuccessViewed)({
      ...args,
      isMember,
      user_id,
    });
  };

  return {
    triggerIfIsaCtaViewed,
    triggerIfIsaCtaClicked,
    triggerIfIsaCtaDismissed,
    triggerIfIsaPermanentPageViewed,
    triggerIfIsaDescriptionPageViewed,
    triggerIfIsaProceedToDeclarationClicked,
    triggerIfIsaConfirmRegistrationClicked,
    triggerIfIsaRegistrationSuccessViewed,
  };
};

export const useIfIsaTransferInAnalytics = () => {
  const investor = useSelector(investorSelector);
  const isMember = investor?.isMember;
  const user_id = investor?.id;
  const { transferData } = useTransferNavigation();

  const fireIfIsaTransferInDealViewed = useAnalytics(
    "ifIsaTransferInDealViewed"
  );
  const triggerIfIsaTransferInDealViewed = async () => {
    (await fireIfIsaTransferInDealViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInProviderViewed = useAnalytics(
    "ifIsaTransferInProviderViewed"
  );
  const triggerIfIsaTransferInProviderViewed = async () => {
    (await fireIfIsaTransferInProviderViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInIsaSelectionViewed = useAnalytics(
    "ifIsaTransferInIsaSelectionViewed"
  );
  const triggerIfIsaTransferInIsaSelectionViewed = async () => {
    (await fireIfIsaTransferInIsaSelectionViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInIsaDetailsViewed = useAnalytics(
    "ifIsaTransferInIsaDetailsViewed"
  );
  const triggerIfIsaTransferInIsaDetailsViewed = async () => {
    (await fireIfIsaTransferInIsaDetailsViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInSummaryViewed = useAnalytics(
    "ifIsaTransferInSummaryViewed"
  );
  const triggerIfIsaTransferInSummaryViewed = async () => {
    (await fireIfIsaTransferInSummaryViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInNoticePeriodViewed = useAnalytics(
    "ifIsaTransferInNoticePeriodViewed"
  );
  const triggerIfIsaTransferInNoticePeriodViewed = async () => {
    (await fireIfIsaTransferInNoticePeriodViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInAuthorisationsViewed = useAnalytics(
    "ifIsaTransferInAuthorisationsViewed"
  );
  const triggerIfIsaTransferInAuthorisationsViewed = async () => {
    (await fireIfIsaTransferInAuthorisationsViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInDeclarationViewed = useAnalytics(
    "ifIsaTransferInDeclarationViewed"
  );
  const triggerIfIsaTransferInDeclarationViewed = async () => {
    (await fireIfIsaTransferInDeclarationViewed)({
      isMember,
      user_id,
    });
  };

  const fireIfIsaTransferInRequestSuccessViewed = useAnalytics(
    "ifIsaTransferInRequestSuccessViewed"
  );
  const triggerIfIsaTransferInRequestSuccessViewed = async () => {
    (await fireIfIsaTransferInRequestSuccessViewed)({
      isMember,
      user_id,
      deal_id: transferData.dealId?.toString() || "",

      isa_provider: transferData.currentManagerName || "",
      isa_accounts: (transferData.transactions ?? []).map((x) => ({
        isa_transfer_amount_entered: (x.amount ?? 0).toString(),
        isa_transfer_amount:
          IsaTransferAmountTypeMap[x.transferType as IfIsaTransferType],
        isa_type: x.isaType,
      })) as IfIsaTransferInSubmitFormProps["isa_accounts"],

      isa_notice_period: transferData.noticeDecision || "",
      isa_transfer_instructions: transferData.noticeDecision || "",
    });
  };

  return {
    triggerIfIsaTransferInDealViewed,
    triggerIfIsaTransferInProviderViewed,
    triggerIfIsaTransferInIsaSelectionViewed,
    triggerIfIsaTransferInIsaDetailsViewed,
    triggerIfIsaTransferInSummaryViewed,
    triggerIfIsaTransferInNoticePeriodViewed,
    triggerIfIsaTransferInAuthorisationsViewed,
    triggerIfIsaTransferInDeclarationViewed,
    triggerIfIsaTransferInRequestSuccessViewed,
  };
};
